import type { Course, AppSettings, ScenarioCourse, ScenarioResult } from '@gpa-scales/shared-types';
import { GradeFormat, CourseType, Term, AGSubject } from '@gpa-scales/shared-types';
import { calculateAllGPA } from './gpaCalculations';

function scenarioToCourse(sc: ScenarioCourse, grade: string): Course {
  return {
    id: sc.courseId ?? `hypothetical-${Math.random().toString(36).slice(2)}`,
    name: sc.name,
    gradeLevel: sc.gradeLevel,
    term: sc.term,
    academicYear: sc.academicYear,
    grade,
    gradeFormat: GradeFormat.Letter,
    credits: sc.credits,
    courseType: sc.courseType,
    agSubject: sc.agSubject,
    isRepeated: false,
  };
}

export function simulateFutureGrades(
  courses: Course[],
  hypotheticalCourses: ScenarioCourse[],
  settings: AppSettings
): ScenarioResult {
  // Current GPA
  const currentVariants = calculateAllGPA(courses, settings);
  const currentGPA: Record<string, number> = {};
  for (const v of currentVariants) {
    currentGPA[v.label] = v.value;
  }

  // Merge: convert hypothetical to courses with their grades
  const hypotheticalAsCourses = hypotheticalCourses.map((sc) =>
    scenarioToCourse(sc, sc.hypotheticalGrade)
  );

  const merged = [...courses, ...hypotheticalAsCourses];
  const projectedVariants = calculateAllGPA(merged, settings);
  const projectedGPA: Record<string, number> = {};
  for (const v of projectedVariants) {
    projectedGPA[v.label] = v.value;
  }

  // Compute deltas
  const delta: Record<string, number> = {};
  for (const label of Object.keys(projectedGPA)) {
    delta[label] = projectedGPA[label] - (currentGPA[label] ?? 0);
  }

  return { currentGPA, projectedGPA, delta };
}

export const GRADE_ORDER = ['A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C', 'C-', 'D+', 'D', 'D-', 'F'];

export function reverseEngineerTargetGPA(
  courses: Course[],
  remainingSlots: number,
  targetGPA: number,
  targetVariant: string,
  settings: AppSettings
): { possible: boolean; requiredGrades: string[]; projectedGPA: number } {
  // Try grades from highest down to see if target is achievable
  for (let numA = remainingSlots; numA >= 0; numA--) {
    for (const gradeIdx of Array.from({ length: GRADE_ORDER.length }, (_, i) => i)) {
      // Create a trial set of grades all at the same level
      const trialGrades = Array(remainingSlots).fill(GRADE_ORDER[gradeIdx]);
      const trialCourses: Course[] = trialGrades.map((grade: string, i: number) => ({
        id: `trial-${i}`,
        name: `Trial Course ${i + 1}`,
        gradeLevel: 11,
        term: Term.Fall,
        academicYear: '2025-2026',
        grade,
        gradeFormat: GradeFormat.Letter,
        credits: 1.0,
        courseType: CourseType.Standard,
        agSubject: AGSubject.C,
        isRepeated: false,
      }));

      const allCourses = [...courses, ...trialCourses];
      const variants = calculateAllGPA(allCourses, settings);
      const targetVariantResult = variants.find((v) => v.label === targetVariant);

      if (targetVariantResult && targetVariantResult.value >= targetGPA) {
        return {
          possible: true,
          requiredGrades: trialGrades,
          projectedGPA: targetVariantResult.value,
        };
      }
    }
  }

  // Check if even all A's is enough
  const allAs = Array(remainingSlots).fill('A');
  const maxCourses: Course[] = allAs.map((grade: string, i: number) => ({
    id: `trial-max-${i}`,
    name: `Trial ${i + 1}`,
    gradeLevel: 11,
    term: Term.Fall,
    academicYear: '2025-2026',
    grade,
    gradeFormat: GradeFormat.Letter,
    credits: 1.0,
    courseType: CourseType.Standard,
    agSubject: AGSubject.C,
    isRepeated: false,
  }));

  const maxVariants = calculateAllGPA([...courses, ...maxCourses], settings);
  const maxResult = maxVariants.find((v) => v.label === targetVariant);

  return {
    possible: false,
    requiredGrades: allAs,
    projectedGPA: maxResult?.value ?? 0,
  };
}

export function modelCourseSwap(
  courses: Course[],
  courseIdToReplace: string,
  replacementCourse: Omit<Course, 'id'>,
  settings: AppSettings
): ScenarioResult {
  // Current GPA
  const currentVariants = calculateAllGPA(courses, settings);
  const currentGPA: Record<string, number> = {};
  for (const v of currentVariants) {
    currentGPA[v.label] = v.value;
  }

  // Swap
  const swapped = courses.map((c) =>
    c.id === courseIdToReplace ? { ...replacementCourse, id: c.id } : c
  );

  const projectedVariants = calculateAllGPA(swapped, settings);
  const projectedGPA: Record<string, number> = {};
  for (const v of projectedVariants) {
    projectedGPA[v.label] = v.value;
  }

  const delta: Record<string, number> = {};
  for (const label of Object.keys(projectedGPA)) {
    delta[label] = projectedGPA[label] - (currentGPA[label] ?? 0);
  }

  return { currentGPA, projectedGPA, delta };
}
